import type { DnaOverlay } from './api';

/**
 * Pure "which DNA reveal fields does Refine actually show" decision (LANE
 * DNA-BUILD, Phase 0). Same house idiom as refineThreadsSource.ts (this
 * file's own sibling, same overlay object) / welcomeBack.ts / quickStart
 * Guide.ts — framework-free, unit-testable standalone.
 *
 * THE DECISION THIS MODULE PINS: the "Threads Meira is noticing" card gets
 * three more fields on the SAME shelf (docs/design/
 * dna-interview-port-2026-07-05.md §3 Phase 0's own placement call) — the
 * through-line prose, the positioning spine, and origins — read ONLY from
 * the served DNA overlay's `profile.throughLine` / `.positioningSpine` /
 * `.origins`. `drivers` is intentionally excluded from THIS resolver (it
 * renders on the interview screen's SynthesisPanel instead, alongside the
 * full four-field reveal) to keep the Refine shelf's three-field summary
 * from crowding into a duplicate of the full synthesis panel.
 *
 * A null overlay.profile (no DNA-synthesis pass yet) or a profile with every
 * field null (a synthesis pass that produced nothing usable) both resolve to
 * an empty result — the honest "keep talking with Meira" state, never a
 * fabricated placeholder standing in.
 */
export interface RefineDnaReveal {
  throughLine: string | null;
  positioningSpine: string | null;
  origins: string | null;
}

/** Resolve the three reveal fields to show, or null when there is genuinely
 *  no signal yet (no synthesis pass, or a pass whose fields are all null). */
export function resolveRefineDnaReveal(overlay: DnaOverlay | null): RefineDnaReveal | null {
  const profile = overlay?.profile ?? null;
  if (!profile) return null;
  const { throughLine, positioningSpine, origins } = profile;
  if (!throughLine && !positioningSpine && !origins) return null;
  return { throughLine, positioningSpine, origins };
}
